import { useState } from 'react';
import { ShieldCheck, Eye, MessageCircleQuestion, FileSearch, FileArchive, ShieldAlert, Radio, Gauge } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ComplianceOverviewTab } from '@/pages/compliance/ComplianceOverviewTab';
import { AuditTrailTab } from '@/pages/compliance/AuditTrailTab';
import { AccessLogTab } from '@/pages/compliance/AccessLogTab';
import { QueriesTab } from '@/pages/compliance/QueriesTab';
import { RemoteViewTab } from '@/pages/compliance/RemoteViewTab';
import { RemoteMonitoringTab } from '@/pages/compliance/RemoteMonitoringTab';
import { SafetyTab } from '@/pages/compliance/SafetyTab';
import { CsrCreateModal } from '@/components/CsrCreateModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { RoleGate } from '@/components/RoleGate';
import {
  CAN_CREATE_CSR_EXPORT,
  CAN_VIEW_AUDIT_TRAIL,
  CAN_VIEW_ACCESS_LOG,
  CAN_VIEW_QUERIES,
} from '@/lib/permissions';
import { useAuth, type UserRole } from '@/contexts/AuthContext';
import { useMyPermissions } from '@/hooks/useMyPermissions';

type TabKey = 'overview' | 'audit' | 'access' | 'queries' | 'safety' | 'monitoring' | 'remote';

/**
 * Harish, 1 Oct 2026: Platform and Application Admin keep only the audit and
 * access logs here; Compliance keeps Overview, Audit Trail, Access Logs and
 * Remote View, and loses Monitor Queries and Safety (compliance areas only).
 * Mirrors handlers/compliance/overview.ts and the clinical-area rules.
 */
const OVERVIEW_ROLES: readonly UserRole[] = [
  'compliance',
  'monitor', 'auditor', 'site_admin',
  'principal_investigator', 'sub_investigator', 'coordinator', 'regulatory_specialist',
] as const;

/**
 * Phase 6 AC1 — the Remote Monitoring View is CRA-facing SDV surface.
 * Distinct from the Sprint-11 external-reviewer "Remote View" tab
 * (which magic-links a sponsor-side reviewer). Both stay visible; role
 * enforcement decides which one the caller actually sees.
 */
const REMOTE_MONITORING_ROLES: readonly UserRole[] = [
  'monitor', 'site_admin', 'compliance',
] as const;

/** Adverse events are patient data: the study team and granted monitors/auditors. */
const SAFETY_ROLES: readonly UserRole[] = [
  'coordinator', 'principal_investigator', 'sub_investigator',
  'regulatory_specialist', 'monitor', 'auditor',
] as const;

/**
 * Remote View: Compliance manages PHI grants for remote viewing; monitors and
 * auditors switch their own remote view on (CAN_TOGGLE_REMOTE_VIEW).
 */
const REMOTE_VIEW_TAB_ROLES: readonly UserRole[] = ['monitor', 'auditor', 'compliance'] as const;

/**
 * `op` is the backend operation the tab's endpoint is guarded by. Where a tab
 * has one, the RBAC grid decides and `roles` is only the fallback for the
 * moment before /me/permissions answers.
 *
 * Only tabs whose endpoint is guarded by a SINGLE `requireOperation` carry one.
 * Monitor Queries deliberately does not: `queries.read` admits every site role
 * because `requireStudyAccess` narrows it per study, so gating the tab on the
 * site-level operation alone would offer it to people the study gate refuses.
 */
const ALL_TABS: Array<{
  key: TabKey;
  label: string;
  icon: React.ElementType;
  roles: readonly UserRole[];
  op?: string;
}> = [
  { key: 'overview', label: 'Overview', icon: Gauge, roles: OVERVIEW_ROLES },
  { key: 'audit', label: 'Site Audit Trail', icon: FileSearch, roles: CAN_VIEW_AUDIT_TRAIL, op: 'compliance.audit_trail' },
  { key: 'access', label: 'Access Logs', icon: Eye, roles: CAN_VIEW_ACCESS_LOG, op: 'compliance.access_log' },
  { key: 'queries', label: 'Monitor Queries', icon: MessageCircleQuestion, roles: CAN_VIEW_QUERIES, op: 'queries.read' },
  { key: 'safety', label: 'Safety (AE / SAE)', icon: ShieldAlert, roles: SAFETY_ROLES },
  { key: 'monitoring', label: 'Remote Monitoring View', icon: Radio, roles: REMOTE_MONITORING_ROLES },
  { key: 'remote', label: 'Remote View', icon: ShieldCheck, roles: REMOTE_VIEW_TAB_ROLES },
];

export function ComplianceHubPage() {
  const { user } = useAuth();
  const perms = useMyPermissions();
  // DREAM-13 AC11 / UI-Permission-Rules.md: hide-not-warn. Only render tabs
  // the current user's role grants access to. The previous behaviour rendered
  // every tab and let each tab show a "you need to be a site_admin" banner —
  // rejected by the product owner during Sprint 1 demo (15:18).
  // Not memoised: holdsOperation closes over the permissions query, so a dep
  // array that tracked it honestly would change every render anyway. Filtering
  // seven items costs nothing.
  const visibleTabs = user
    ? ALL_TABS.filter((t) =>
        t.op ? perms.holdsOperation(t.op, t.roles) : t.roles.includes(user.role as UserRole),
      )
    : [];
  const [tab, setTab] = useState<TabKey>(visibleTabs[0]?.key ?? 'audit');
  const [csrOpen, setCsrOpen] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance Hub"
        subtitle={`${user?.siteNames?.[user.activeSiteId] ?? 'This site'} — audit trail, access logs, monitor queries, remote monitoring.`}
        actions={
          <RoleGate roles={CAN_CREATE_CSR_EXPORT}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setCsrOpen(true)}
              title="Bundle eCRF, audit trail, queries and deviations into a ZIP"
            >
              <FileArchive className="h-4 w-4" /> Export CSR Prep
            </button>
          </RoleGate>
        }
      />

      <CsrCreateModal
        open={csrOpen}
        onClose={() => setCsrOpen(false)}
      />

      <div className="border-b border-surface-border">
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Compliance tabs">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap border-b-2 px-5 py-3.5 text-sm font-semibold',
                tab === key
                  ? 'border-accent-600 text-accent-700'
                  : 'border-transparent text-ink-secondary hover:text-ink-primary',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && visibleTabs.some((t) => t.key === 'overview') && <ComplianceOverviewTab onOpenTab={setTab} />}
      {tab === 'audit' && visibleTabs.some((t) => t.key === 'audit') && <AuditTrailTab />}
      {tab === 'access' && visibleTabs.some((t) => t.key === 'access') && <AccessLogTab />}
      {tab === 'queries' && visibleTabs.some((t) => t.key === 'queries') && <QueriesTab />}
      {tab === 'safety' && visibleTabs.some((t) => t.key === 'safety') && <SafetyTab />}
      {tab === 'monitoring' && visibleTabs.some((t) => t.key === 'monitoring') && <RemoteMonitoringTab />}
      {tab === 'remote' && visibleTabs.some((t) => t.key === 'remote') && <RemoteViewTab />}
    </div>
  );
}
